import axios from 'axios';
import qs from 'qs';
import { ApiResult, Filter, ServiceResponse } from '@/OptimusRoutes/interfaces';
import {
	CheckPhoneStatusQueryDto,
	CheckPhoneStatusResponseDto,
	EmailLogEventHistoryResponseDto,
	GetEmailLogEventHistoryQueryDto,
	GetEmailLogEventsResponseDto,
	GetEmailLogsResponseDto,
	GetEmailTypesResponseDto,
	OutboundEmailLog,
	UpdatePhoneStatusBodyDto,
	UpdatePhoneStatusResponseDto,
} from './diagnostics.dto';

type Service = {
	getPhoneStatus: ({
		email,
		phoneNumber,
	}: CheckPhoneStatusQueryDto) => Promise<ServiceResponse<CheckPhoneStatusResponseDto>>;
	updatePhoneStatus: ({
		phoneNumber,
		platform,
		clientId,
	}: UpdatePhoneStatusBodyDto) => Promise<ServiceResponse<UpdatePhoneStatusResponseDto>>;
	getEmailLogs: (filter: Filter<OutboundEmailLog>) => Promise<ApiResult<GetEmailLogsResponseDto[]>>;
	getEmailLogEventHistory: (
		params: GetEmailLogEventHistoryQueryDto
	) => Promise<ServiceResponse<EmailLogEventHistoryResponseDto[]>>;
	getEmailLogEvents: (filter: Filter<OutboundEmailLog>) => Promise<ApiResult<GetEmailLogEventsResponseDto[]>>;
	getEmailTypes: () => Promise<GetEmailTypesResponseDto[]>;
};

const getPhoneStatus = async ({
	email,
	phoneNumber,
}: CheckPhoneStatusQueryDto): Promise<ServiceResponse<CheckPhoneStatusResponseDto>> => {
	let params: CheckPhoneStatusQueryDto = {
		email,
	};

	if (phoneNumber) {
		params = {
			...params,
			phoneNumber,
		};
	}
	const { data } = await axios.get(`/v2/diagnostics/phone-status/`, {
		params,
	});

	return data;
};

const updatePhoneStatus = async ({
	phoneNumber,
	platform,
	clientId,
}: UpdatePhoneStatusBodyDto): Promise<ServiceResponse<UpdatePhoneStatusResponseDto>> => {
	const { data } = await axios.patch(`/v2/diagnostics/phone-status/`, {
		phoneNumber,
		platform,
		clientId,
	});

	return data;
};

const getEmailLogs = async ({
	filter,
	take,
	skip,
}: Filter<OutboundEmailLog>): Promise<ApiResult<GetEmailLogsResponseDto[]>> => {
	const params = { filter: JSON.stringify(filter), skip, take };
	const { data: response } = await axios.get<ServiceResponse<ApiResult<GetEmailLogsResponseDto[]>>>(
		`/v2/diagnostics/email-logs?${qs.stringify(params)}`
	);

	if (!response.error && response.success) {
		return response.success;
	}

	return { count: 0, data: [] };
};

const getEmailLogEventHistory = async ({
	providerMessageId,
	recipientEmail,
}: GetEmailLogEventHistoryQueryDto): Promise<ServiceResponse<EmailLogEventHistoryResponseDto[]>> => {
	const { data } = await axios.get(`/v2/diagnostics/email-logs/${providerMessageId}/event-history/${recipientEmail}`);

	return data;
};

const getEmailLogEvents = async ({
	filter,
	take,
	skip,
}: Filter<OutboundEmailLog>): Promise<ApiResult<GetEmailLogEventsResponseDto[]>> => {
	const params = { filter: JSON.stringify(filter), skip, take };
	const { data: response } = await axios.get<ServiceResponse<ApiResult<GetEmailLogEventsResponseDto[]>>>(
		`/v2/diagnostics/email-logs/events?${qs.stringify(params)}`
	);

	if (!response.error && response.success) {
		return response.success;
	}

	return { count: 0, data: [] };
};

const getEmailTypes = async (): Promise<GetEmailTypesResponseDto[]> => {
	const { data: response } = await axios.get<ServiceResponse<GetEmailTypesResponseDto[]>>(
		'/v2/diagnostics/email-logs/types'
	);

	if (!response.error && response.success) {
		return response.success;
	}

	return [];
};

const DiagnosticsService: Service = {
	getPhoneStatus,
	updatePhoneStatus,
	getEmailLogs,
	getEmailLogEventHistory,
	getEmailLogEvents,
	getEmailTypes,
};

export default DiagnosticsService;
